import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { AktaLogo } from '../components/AktaLogo';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent } from '../components/ui/card';
import { Plus, Trash2, Check, ShieldCheck } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { GOAL_TYPES } from '../mock';

const numOrEmpty = (v) => (v === '' || v === null || v === undefined ? '' : String(v));

const Field = ({ id, label, hint, children }) => (
  <div>
    <Label htmlFor={id} className="text-brand-indigo font-semibold">{label}</Label>
    {children}
    {hint && <div className="text-[11px] text-muted-foreground mt-1">{hint}</div>}
  </div>
);

const PrefillPage = () => {
  const { sid } = useParams();
  const [sp] = useSearchParams();
  const token = sp.get('token') || '';

  const [state, setState] = useState({ loading: true, error: null, submitted: false, data: null, rmName: '', clientName: '' });
  const [form, setForm] = useState({
    name: '', age: '', city: '', dependents: '',
    monthly_income: '', monthly_expense: '', monthly_emi: '', monthly_sip: '',
    goals: [],
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) {
      setState({ loading: false, error: 'Missing token. Ask your RM to resend the link.', submitted: false });
      return;
    }
    api.getPrefill(sid, token)
      .then((res) => {
        const f = res.fields || {};
        setForm({
          name: f.name ?? res.client?.name ?? '',
          age: numOrEmpty(f.age),
          city: f.city ?? res.client?.city ?? '',
          dependents: f.dependents ?? '',
          monthly_income: numOrEmpty(f.monthly_income),
          monthly_expense: numOrEmpty(f.monthly_expense),
          monthly_emi: numOrEmpty(f.monthly_emi),
          monthly_sip: numOrEmpty(f.monthly_sip),
          goals: Array.isArray(f.goals) ? f.goals : [],
        });
        setState({
          loading: false, error: null, submitted: !!res.already_submitted,
          rmName: res.rm_name || '', clientName: res.client?.name || ''
        });
      })
      .catch((e) => setState({ loading: false, error: e.apiMessage || 'Link is invalid or has been revoked.', submitted: false }));
  }, [sid, token]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const addGoal = () => setForm((p) => ({ ...p, goals: [...p.goals, { id: `g-${Date.now()}`, type: "Child's Education", amount: '', years: '', priority: 'Primary' }] }));
  const updateGoal = (id, patch) => setForm((p) => ({ ...p, goals: p.goals.map((g) => g.id === id ? { ...g, ...patch } : g) }));
  const removeGoal = (id) => setForm((p) => ({ ...p, goals: p.goals.filter((g) => g.id !== id) }));

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name?.trim() || null,
        age: form.age === '' ? null : Number(form.age),
        city: form.city?.trim() || null,
        dependents: form.dependents || null,
        monthly_income: form.monthly_income === '' ? null : Number(form.monthly_income),
        monthly_expense: form.monthly_expense === '' ? null : Number(form.monthly_expense),
        monthly_emi: form.monthly_emi === '' ? null : Number(form.monthly_emi),
        monthly_sip: form.monthly_sip === '' ? null : Number(form.monthly_sip),
        goals: form.goals.map((g) => ({ ...g, amount: Number(g.amount) || 0, years: Number(g.years) || 0 })),
      };
      await api.submitPrefill(sid, token, payload);
      setState((p) => ({ ...p, submitted: true }));
      toast({ title: 'Thank you!', description: 'Your responses reached your relationship manager.' });
    } catch (e) {
      toast({ title: 'Could not submit', description: e.apiMessage || 'Try again', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (state.loading) {
    return <div className="min-h-screen flex items-center justify-center text-brand-indigo">Loading…</div>;
  }
  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-brand-bg">
        <Card className="max-w-md w-full border-brand-border">
          <CardContent className="p-6 text-center space-y-3">
            <div className="text-lg font-bold text-brand-indigo">Link unavailable</div>
            <div className="text-sm text-muted-foreground">{state.error}</div>
          </CardContent>
        </Card>
      </div>
    );
  }
  if (state.submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-brand-bg">
        <Card className="max-w-lg w-full border-brand-border" data-testid="prefill-thankyou">
          <CardContent className="p-8 text-center space-y-4">
            <div className="mx-auto h-14 w-14 rounded-full bg-emerald-50 border border-emerald-300 flex items-center justify-center">
              <Check className="h-7 w-7 text-emerald-600" />
            </div>
            <div className="text-xl font-bold text-brand-indigo">Thank you</div>
            <div className="text-sm text-muted-foreground">
              Your pre-meeting details have been sent to <span className="font-semibold text-brand-indigo">{state.rmName || 'your relationship manager'}</span>. They will walk into your session with the data ready.
            </div>
            <div className="text-[11px] text-muted-foreground pt-4 border-t border-brand-border">
              Akta Wealth · AMFI-registered Mutual Fund Distributor · ARN-47282
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-bg py-8 px-4" data-testid="prefill-page">
      <div className="max-w-2xl mx-auto space-y-5">
        <div className="flex items-center gap-3">
          <AktaLogo size={40} showText={false} />
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Pre-Meeting Form</div>
            <div className="text-xl font-bold text-brand-indigo">Akta Wealth</div>
          </div>
        </div>

        <Card className="border-brand-border">
          <CardContent className="p-6 space-y-2">
            <div className="text-lg font-bold text-brand-indigo">Hello {state.clientName || 'there'}</div>
            <div className="text-sm text-muted-foreground leading-relaxed">
              Your relationship manager {state.rmName ? <span className="font-semibold text-brand-indigo">{state.rmName}</span> : ''} has set up a Financial MRI session for you.
              Please share a few details so they can prepare a more useful review.
              This form takes ~5 minutes. Nothing shared here is a commitment or investment.
            </div>
            <div className="flex items-center gap-2 pt-3 text-[11px] text-muted-foreground">
              <ShieldCheck className="h-3.5 w-3.5" /> All responses are stored in Akta's secure audit trail.
            </div>
          </CardContent>
        </Card>

        <Card className="border-brand-border">
          <CardContent className="p-6 space-y-5">
            <div className="text-sm font-semibold text-brand-indigo">About you</div>
            <div className="grid md:grid-cols-2 gap-4">
              <Field id="name" label="Full name">
                <Input id="name" value={form.name} onChange={(e) => set('name', e.target.value)} className="mt-1 h-11" placeholder="As per PAN" />
              </Field>
              <Field id="age" label="Age">
                <Input id="age" type="number" value={form.age} onChange={(e) => set('age', e.target.value)} className="mt-1 h-11" placeholder="41" />
              </Field>
              <Field id="city" label="City">
                <Input id="city" value={form.city} onChange={(e) => set('city', e.target.value)} className="mt-1 h-11" placeholder="Surat" />
              </Field>
              <Field id="dependents" label="Dependents">
                <Select value={form.dependents} onValueChange={(v) => set('dependents', v)}>
                  <SelectTrigger className="mt-1 h-11"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">None</SelectItem>
                    <SelectItem value="1">1</SelectItem>
                    <SelectItem value="2">2</SelectItem>
                    <SelectItem value="3+">3+</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </CardContent>
        </Card>

        <Card className="border-brand-border">
          <CardContent className="p-6 space-y-5">
            <div className="text-sm font-semibold text-brand-indigo">Monthly cash flow (approximate)</div>
            <div className="grid md:grid-cols-2 gap-4">
              <Field id="mi" label="Household income (₹ / month)">
                <Input id="mi" type="number" value={form.monthly_income} onChange={(e) => set('monthly_income', e.target.value)} className="mt-1 h-11" placeholder="150000" />
              </Field>
              <Field id="me" label="Living expenses (₹ / month)">
                <Input id="me" type="number" value={form.monthly_expense} onChange={(e) => set('monthly_expense', e.target.value)} className="mt-1 h-11" placeholder="60000" />
              </Field>
              <Field id="mem" label="EMI outgo (₹ / month)">
                <Input id="mem" type="number" value={form.monthly_emi} onChange={(e) => set('monthly_emi', e.target.value)} className="mt-1 h-11" placeholder="0" />
              </Field>
              <Field id="ms" label="Current SIP (₹ / month)">
                <Input id="ms" type="number" value={form.monthly_sip} onChange={(e) => set('monthly_sip', e.target.value)} className="mt-1 h-11" placeholder="10000" />
              </Field>
            </div>
          </CardContent>
        </Card>

        <Card className="border-brand-border">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-brand-indigo">Goals you are saving for</div>
              <Button variant="outline" size="sm" onClick={addGoal} className="border-dashed border-brand-indigo/40 text-brand-indigo" data-testid="prefill-add-goal">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add goal
              </Button>
            </div>
            {form.goals.length === 0 && (
              <div className="text-[12px] text-muted-foreground">Add each goal separately — e.g. child's education, home purchase, retirement. Don't worry about precise numbers, best estimates are fine.</div>
            )}
            <div className="space-y-3">
              {form.goals.map((g) => (
                <div key={g.id} className="border border-brand-border rounded-lg p-3 grid md:grid-cols-12 gap-2 items-end" data-testid={`prefill-goal-${g.id}`}>
                  <div className="md:col-span-5">
                    <Label className="text-brand-indigo text-xs font-semibold">Goal</Label>
                    <Select value={g.type} onValueChange={(v) => updateGoal(g.id, { type: v })}>
                      <SelectTrigger className="mt-1 h-10"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {GOAL_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-3">
                    <Label className="text-brand-indigo text-xs font-semibold">Target (₹)</Label>
                    <Input type="number" value={g.amount} onChange={(e) => updateGoal(g.id, { amount: e.target.value })} className="mt-1 h-10" placeholder="1000000" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-brand-indigo text-xs font-semibold">Years</Label>
                    <Input type="number" value={g.years} onChange={(e) => updateGoal(g.id, { years: e.target.value })} className="mt-1 h-10" placeholder="5" />
                  </div>
                  <div className="md:col-span-2 flex justify-end">
                    <Button variant="ghost" size="icon" onClick={() => removeGoal(g.id)} className="text-brand-red hover:bg-red-50"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2 sticky bottom-4">
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-brand-green hover:bg-brand-greenDark text-white h-11 px-6 shadow-lg"
            data-testid="prefill-submit"
          >
            <Check className="h-4 w-4 mr-2" /> {saving ? 'Submitting…' : 'Send to my RM'}
          </Button>
        </div>

        <div className="text-[10px] text-center text-muted-foreground pt-2 pb-6">
          Akta Wealth · AMFI-registered Mutual Fund Distributor · ARN-47282 · Mutual fund investments are subject to market risks.
        </div>
      </div>
    </div>
  );
};

export default PrefillPage;
